import React from 'react';
import styled from 'styled-components';
import { colors } from '../../../assets/styles/GlobalStyles';
import { SearchableSelect } from '../../../components/ui/SearchableSelect/SearchableSelect';
import { rotuloRegistroCep } from '../utils/cepRegistros';

// Acima disso, a lista de opções fica comprida demais para a tela (principalmente no
// celular), então troca por um select com busca.
const LIMITE_LISTA = 8;

const Container = styled.div`
  margin: -0.4rem 0 1.2rem;
  padding: 0.9rem;
  border: 2px solid ${props => props.$pendente ? colors.lightBlue : colors.borderLight};
  border-radius: 8px;
  background: ${colors.backgroundLight};
`;

const Titulo = styled.p`
  margin: 0 0 0.6rem;
  color: ${colors.darkBlue};
  font-weight: 600;
  font-size: 0.9rem;
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Opcao = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${props => props.$selecionada ? colors.darkBlue : colors.borderLight};
  border-radius: 6px;
  background: ${props => props.$selecionada ? 'rgba(13, 35, 87, 0.06)' : colors.white};
  color: ${colors.darkGray};
  font-size: 0.9rem;
  line-height: 1.35;
  cursor: pointer;

  &:hover { border-color: ${colors.lightBlue}; }

  input {
    margin-top: 0.15rem;
    accent-color: ${colors.blueDark};
    flex-shrink: 0;
  }
`;

/**
 * Lista os registros de um CEP que tem mais de um endereço cadastrado (CEP único de
 * localidade) para a pessoa escolher o dela. Não renderiza nada com 0 ou 1 registro
 * (nesse caso o preenchimento é automático).
 *
 * @param {string} props.name - Nome base do grupo de rádios (ex: nome do campo de CEP)
 * @param {Array} props.opcoes - Registros devolvidos pela API de Endereço
 * @param {number|string} props.selecionadoId - `id` do registro escolhido, se houver
 * @param {Function} props.onSelect - Recebe o registro escolhido
 * @param {boolean} [props.disabled]
 */
export const CepOpcoes = ({ name, opcoes = [], selecionadoId, onSelect, disabled }) => {
  if (opcoes.length < 2) return null;

  const pendente = !opcoes.some((r) => String(r.id) === String(selecionadoId));
  const titulo = pendente
    ? 'Encontramos mais de um endereço para este CEP. Selecione o seu:'
    : 'Endereço selecionado para este CEP (você pode trocar):';

  if (opcoes.length > LIMITE_LISTA) {
    return (
      <Container $pendente={pendente}>
        <SearchableSelect
          label={titulo}
          name={`${name}Opcao`}
          value={pendente ? '' : String(selecionadoId)}
          options={opcoes}
          onChange={() => {}}
          onSelectOption={(registro) => registro && onSelect(registro)}
          getOptionLabel={rotuloRegistroCep}
          getOptionValue={(registro) => (registro ? String(registro.id) : '')}
          placeholder="Digite o logradouro ou bairro..."
          noOptionsMessage="Nenhum endereço encontrado"
          disabled={disabled}
        />
      </Container>
    );
  }

  return (
    <Container $pendente={pendente} role="radiogroup" aria-label={titulo}>
      <Titulo>{titulo}</Titulo>
      <Lista>
        {opcoes.map((registro) => {
          const selecionada = String(registro.id) === String(selecionadoId);
          return (
            <Opcao key={registro.id} $selecionada={selecionada}>
              <input
                type="radio"
                name={`${name}Opcao`}
                value={registro.id}
                checked={selecionada}
                onChange={() => onSelect(registro)}
                disabled={disabled}
              />
              {rotuloRegistroCep(registro)}
            </Opcao>
          );
        })}
      </Lista>
    </Container>
  );
};
